import React, {useEffect, useState} from "react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import clsx from "clsx";
import Loading from "../helpers/Loading.jsx";
import Menu from "../helpers/Menu.jsx";
import ConfirmModal from "../helpers/modals/ConfirmModal";
import {getProductsData, getRoomData, setProductApplication} from "./_requests.jsx";
import Visualizer from "./visualizer/Visualizer.jsx";
import RoomActions from "./visualizer/toolbars/RoomActions.jsx";
import VisualizerToolbar from "./visualizer/toolbars/VisualizerToolbar";
import ProductList from "./products/ProductList.jsx";
import ProductListHorizontal from "./products/ProductListHorizontal";
import ProductConfigs from "./visualizer/toolbars/ProductConfigs";

export default function App({room, productsQuery, _handleBackToRooms}) {
    const [ roomData, setRoomData ] = useState();
    const [ roomActionsProps, setRoomActionsProps ] = useState({});
    const [ activeProduct, setActiveProduct ] = useState(productsQuery.active);
    const [ startWithActiveProduct, setStartWithActiveProduct ] = useState(room.startWithActiveProduct && productsQuery.active > 0 ? true : false);
    const [ selectedSurfaces, setSelectedSurfaces ] = useState({});
    const [ updateWalls, setUpdateWalls ] = useState(0);
    const [ productsPage, setProductsPage ] = useState(1);

    const [ showSelectors, setShowSelectors ] = useState(true);
    const _onShowSelectorsChange = () => {
        setShowSelectors(showSelectors ? false : true);
    };
    const [ productConfigsChanged, setProductConfigsChanged ] = useState(false);
    const [ productConfigs, setProductConfigs ] = useState({
        show: false,
        notification: false,
        active: [],
        ids: [],
        reset: false
    });
    const _onProductConfigsChange = (element, value) => {
        let temp = productConfigs;
        if(element == 'show') temp.show = temp.show ? false : true;
        if(element == 'reset') temp.reset = value;
        if(element == 'active') {
            temp.active = value;
            temp.ids=[];
            for(let i=0; i<value.length; i++) {
                temp.ids.push(value[i].id);
            }
            // set notification true when there is a new active product
            if(temp.active.length > 0) temp.notification = true;
        }
        // set notification false on show
        if(temp.show) temp.notification = false;
        setProductConfigs(temp);
        setProductConfigsChanged(productConfigsChanged ? false : true);
    };

    // to use for update products on surface even when active product does not change
    const [ clickProduct, setClickProduct ] = useState(0);

    // for filter query change
    const [ productsFilterChanged, setProductsFilterChanged ] = useState(false);
    const [ productsFilter, setProductsFilter ] = useState(productsQuery);
    const _onProductsFilterChange = (filterOptions) => {
        setProductsFilter(filterOptions);
        setProductsFilterChanged(productsFilterChanged ? false : true)
    };

    /**
     * Adapt the layout by device
     */
    const documentData = {
        width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
        height: window.innerHeight|| document.documentElement.clientHeight || document.body.clientHeight,
    }
    documentData.portrait = documentData.height > documentData.width;
    documentData.menuAxis = 'x';
    if(!documentData.portrait && documentData.height < 480) documentData.menuAxis = 'y';
    documentData.horizontalBlock = false;
    if(documentData.portrait || documentData.width < 1200) documentData.horizontalBlock = true;
    const [ deviceData, setDeviceData ] = useState(documentData);
    window.addEventListener('resize', () => {
        const documentData = {
            width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
            height: window.innerHeight|| document.documentElement.clientHeight || document.body.clientHeight,
        }
        documentData.portrait = documentData.height > documentData.width;
        documentData.menuAxis = 'x';
        if(!documentData.portrait && documentData.height < 480) documentData.menuAxis = 'y';
        documentData.horizontalBlock = false;
        if(documentData.portrait || documentData.width < 1200) documentData.horizontalBlock = true;
        setDeviceData(documentData);
    });

    /**
     * Handle errors
     */
    function destroyVisualizerData() {
        setRoomData({});
        setRoomActionsProps({});
        setSelectedSurfaces({});
        setUpdateWalls(0);
        setClickProduct(0);
    }
    const _handleLoadingError = (error) => {
        // proces
        //console.log(error)
        _handleBackToRooms(error);
        destroyVisualizerData();
    };

    /**
     * Toogle Full Product list
     */
    const [ fullModalState, setFullModalState ] = useState({
        rooms: false,
        products: false
    });
    const _onFullModalChange = (e, modal, state) => {
        e.preventDefault();
        const temp = {
            rooms: false,
            products: false
        }
        Object.keys(temp).forEach(v => v == modal ? temp[v] = state : temp[v] = false)
        setFullModalState(temp);
    };

    /**
     * Room Data
     */
    const rQuery =
        useQuery({
            queryKey: ['room', room.id],
            queryFn: () => getRoomData(room.id, deviceData),
            placeholderData: keepPreviousData,
            staleTime: 1 * 60 * 1000,
            cacheTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false
        })
    // set room data & selectors
    useEffect(() =>{
        if(rQuery.data) {
            // get wall selectors
            let temp = {wallSelectors : []}
            rQuery.data.walls.details.forEach(element => {
                temp.wallSelectors.push(element.selector)
            });
            setRoomData(rQuery.data)
            setRoomActionsProps(temp)
        }
    }, [rQuery.data])
    // check errors
    useEffect(() =>{
        if(rQuery.error) {
            console.log('process Error')
            _handleBackToRooms(rQuery.error);
            destroyVisualizerData();
        }
    }, [rQuery.error])

    /**
     * Get Available Products
     */
    const pQuery =
        useQuery({
            queryKey: ['products', productsFilter, productsPage],
            queryFn: () => getProductsData(productsFilter, productsPage),
            placeholderData: keepPreviousData,
            staleTime: 1 * 60 * 1000,
            cacheTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false
        })
    // useEffect(() => {
    //     if(!pQuery.isFetching) pQuery.refetch()
    // }, [productsPage])

    // notify server
    const applicationQuery =
        useQuery({
            queryKey: [],
            queryFn: () => setProductApplication(activeProduct, room.id, selectedSurfaces, false),
            refetchOnWindowFocus: false,
            enabled: false
        })

    /**
     * Product change
     */
    // handle active product change
        //todo: pass from onActiveChange to onActiveProductChange and remove the useEffect for clickProduct
    const onActiveChange = (e, id) => {
            // reset the active product
            let elems = document.querySelectorAll(".vzp_product");
            [].forEach.call(elems, function(el) {
                el.classList.remove("active");
            });
            elems = document.querySelectorAll(".vzp_product[data-id='" + id + "']");
            [].forEach.call(elems, function(el) {
                el.classList.add("active");
            });
            e.preventDefault();
            setActiveProduct(id);
            setClickProduct(a => a + 1);

            // check if the product has configs
            let hasConfigs = false;
            for (let i = 0; i < pQuery.data.list.length; i++) {
                let tempProduct = pQuery.data.list[i];
                if (parseInt(tempProduct.id) == id) {
                    if (tempProduct.info.has_association) {
                        _onProductConfigsChange('active', [tempProduct]);
                        hasConfigs = true;
                    }
                    break;
                }
            }
            if (!hasConfigs) _onProductConfigsChange('active', []);

            // reset Product Configuration
            _onProductConfigsChange('reset', true);

            // reset Full Modal State
            const temp = fullModalState;
            Object.keys(temp).forEach(v => temp[v] = false);
            setFullModalState(temp);
        };
    // for full product info
    const onActiveProductChange = (product, isConfig=false) => {
        setActiveProduct(product.id);
        if(selectedSurfaces.walls !== undefined) {
            for (let i = 0; i < selectedSurfaces.walls.length; i++) {
                const wallId = selectedSurfaces.walls[i]
                if(wallId !== undefined) {
                    let temp = roomData;
                    temp.walls.details[wallId].activeProduct = product;
                    setRoomData(temp);
                    setUpdateWalls(product.id);
                }
            }
        }

        if(isConfig) _onProductConfigsChange('reset', false);

        // reset Full Modal State
        const temp = fullModalState;
        Object.keys(temp).forEach(v => temp[v] = false);
        setFullModalState(temp);
    };
    // change active product on selected surfaces
    useEffect(() => {
        if(selectedSurfaces.walls !== undefined) {
            for (let i = 0; i < selectedSurfaces.walls.length; i++) {
                const wallId = selectedSurfaces.walls[i]
                if(wallId !== undefined && pQuery.data != undefined) {
                    let temp = roomData;
                    for(let i=0; i<pQuery.data.list.length; i++) {
                        if(activeProduct == pQuery.data.list[i].id) {
                            temp.walls.details[wallId].activeProduct = pQuery.data.list[i];
                        }
                    }
                    setRoomData(temp);
                    setUpdateWalls(updateWalls + 1);
                }
            }
            applicationQuery.refetch();
        }
    },[clickProduct])

    // Set initial active product
    useEffect(() => {
        if(startWithActiveProduct && pQuery.data != undefined && pQuery.data.list != undefined && selectedSurfaces.walls != undefined) {
            for (let i = 0; i < selectedSurfaces.walls.length; i++) {
                const wallId = selectedSurfaces.walls[i]
                if(wallId !== undefined) {
                    let temp = roomData;
                    for(let i=0; i<pQuery.data.list.length; i++) {
                        if(activeProduct == pQuery.data.list[i].id) {
                            temp.walls.details[wallId].activeProduct = pQuery.data.list[i];
                            // check if the product has configs
                            if (pQuery.data.list[i].info.has_association) _onProductConfigsChange('active', [pQuery.data.list[i]]);
                        }
                    }
                    setRoomData(temp);
                    setUpdateWalls(updateWalls + 1);
                    setStartWithActiveProduct(false);
                }
            }
        }
    },[pQuery])

    /**
     * Selected surface change
     */
    const onSurfaceChange = (surfaces) => {
        // remove the surface that has an active product setted in db. This way I can avoid selecting it.
        for(let i = 0; i < roomData.walls.details.length; i++) {
            if (roomData.walls.details[i].active_product !== undefined && roomData.walls.details[i].active_product !== null) {surfaces.walls.splice(i, 1);}
        }
        setSelectedSurfaces(surfaces);
        if(startWithActiveProduct) setClickProduct(true);
    };

    /**
     * Products Page change handler
     */
    const onProductsPageChange = (e) => {
        e.preventDefault();
        let seletctedPage = e.target.dataset.page;
        // check if clicked element is a child. For example for previous and next buttons we use icons
        if(e.target.tagName.toLowerCase() === 'i') {
            seletctedPage = e.target.parentNode.dataset.page;
        }
        if(seletctedPage !== undefined && seletctedPage != productsPage) {
            setProductsPage(seletctedPage);
        }
    };

    return <>
        <ConfirmModal
            title="Terms Of Use"
            messages={["By using the Hackrea Visualizer you understand that although we do our best to provide a result close to reality, you may obtain a different result when using the products in your interior. The difference can be manifested in: *colors, *size of patterns."]}
            hideDays={7}
            _onClose={_handleBackToRooms}
        />
        <div id="mainContent" className={clsx('bg-light', {'horizontal': deviceData.horizontalBlock}, {'menuY': deviceData.menuAxis == 'y'})}>
            <div id="vz_header" className="p-3">
                <Menu
                    deviceData={deviceData}
                    fullModalState={fullModalState}
                    _handleBackToRooms={_handleBackToRooms}
                    _onFullModalChange={_onFullModalChange}
                />
            </div>
            {
                rQuery.isPending || rQuery.isFetching ? (
                    <Loading background="#eeeeee" />
                ) : rQuery.isError ? (
                    <p className="d-flex justify-content-center align-items-center text-center h-100">Error: {error.message}</p>
                ) : roomData && ([
                    <Visualizer
                        key={1}
                        room={room}
                        roomData={roomData}
                        updateWalls={updateWalls}
                        _handleLoadingError={_handleLoadingError}
                    />,
                    <RoomActions
                        key={2}
                        {...roomActionsProps}
                        showSelectors={showSelectors}
                        selectedSurfaces={selectedSurfaces}
                        startWithActiveProduct={startWithActiveProduct}
                        onSurfaceChange={onSurfaceChange}
                    />,
                    <ProductConfigs
                        key={3}
                        activeProducts={[]}
                        productConfigs={productConfigs}
                        showSelectors={showSelectors}
                        _onActiveProductChange={onActiveProductChange}
                    />,
                    <VisualizerToolbar
                        key={4}
                        showSelectors={showSelectors}
                        productConfigs={productConfigs}
                        _onShowSelectorsChange={_onShowSelectorsChange}
                        _onProductConfigsChange={_onProductConfigsChange}
                    />,
                ])
            }
        </div>
        <div id="sidebar" className={clsx({'horizontal': deviceData.horizontalBlock}, {'menuY': deviceData.menuAxis == 'y'})}>
            {
                pQuery.isPending || pQuery.isFetching ? (
                    <Loading />
                ) : pQuery.isError ? (
                    <p className="d-flex justify-content-center align-items-center text-center h-100">Error: {error.message}</p>
                ) : ([
                    deviceData.horizontalBlock &&
                    <ProductListHorizontal
                        key={1}
                        products={pQuery.data}
                        activeProduct={activeProduct}
                        fullModalState={fullModalState}
                        onActiveChange={onActiveChange}
                        _onFullModalChange={_onFullModalChange}
                    />,
                    <ProductList
                        key={2}
                        products={pQuery.data}
                        fullModalState={fullModalState}
                        deviceData={deviceData}
                        activeProduct={activeProduct}
                        productsFilter={productsFilter}
                        onActiveChange={onActiveChange}
                        onProductsPageChange={onProductsPageChange}
                        _onProductsFilterChange={_onProductsFilterChange}
                    />
                ])
            }
        </div>

    </>
}